import React, { useState, useEffect, useRef } from 'react';
import { Send, User, ShieldCheck, Scale, Zap, BarChart3, Info, Mic, Search, Link, X, Copy, Save, Download } from 'lucide-react';
import { lerToken } from '../services/sessao';
import './SaasLizChat.css';

interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
  expertise?: 'law' | 'marketing' | 'education' | 'tech';
  source?: { title: string, url: string };
}

interface SaasLizChatProps {
  onClose?: () => void;
  onDragStart?: (e: React.MouseEvent) => void;
}

export default function SaasLizChat({ onClose, onDragStart }: SaasLizChatProps) {
  const savedName = typeof window !== 'undefined' ? localStorage.getItem('liz_interlocutor_name') || '' : '';
  const [interlocutorName, setInterlocutorName] = useState(savedName);

  const initialGreeting = savedName
    ? `Olá novamente, ${savedName.split(' ')[0]}! 👋 Sou a Liz, pronta para te ajudar. Em que posso colaborar hoje?`
    : 'Olá! Sou a Liz, Super Assistente Estratégica da Alencastro Consultoria. Antes de começarmos, posso saber seu nome? Assim torno nossa conversa mais agradável.';

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: initialGreeting,
      timestamp: new Date(),
    }
  ]);
  const [saveDialogOpen, setSaveDialogOpen] = useState<{ open: boolean; content: string; messageId?: string }>({ open: false, content: '' });
  const [saveDialogTitle, setSaveDialogTitle] = useState('');
  const [saveDialogPurpose, setSaveDialogPurpose] = useState('');
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSearchingWeb, setIsSearchingWeb] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const recognitionRef = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping, isSearchingWeb]);

  // ── Speech-to-Text (STT) ────────────────────────────────────────────────
  const toggleMic = () => {
    const SpeechRecognition: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Seu navegador não suporta reconhecimento de voz. Use Chrome ou Edge.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = 'pt-BR';
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e: any) => {
      const transcript = Array.from(e.results)
        .map((r: any) => r[0].transcript)
        .join('');
      setInput(transcript);
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    rec.start();
    recognitionRef.current = rec;
    setIsListening(true);
  };

  // ── Text-to-Speech (TTS) — voz natural com sentenças encadeadas ─────────
  const pickBestVoice = (): SpeechSynthesisVoice | null => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    const ptVoices = voices.filter(v => /^pt(-|_)?BR/i.test(v.lang) || v.lang.startsWith('pt'));
    if (!ptVoices.length) return null;
    // Ordem de preferência: vozes neurais/online > Google > Microsoft tradicional
    const patterns = [
      /natural/i,             // Microsoft "Online (Natural)" - melhor qualidade
      /neural/i,
      /online/i,
      /google.*portug/i,      // Google Brazilian Portuguese
      /francisca/i,           // Microsoft Francisca Online
      /antonio/i,             // Microsoft Antonio Online
      /maria/i, /luciana/i, /helena/i, /vitória/i, /vitoria/i,
    ];
    for (const pat of patterns) {
      const v = ptVoices.find(x => pat.test(x.name));
      if (v) return v;
    }
    return ptVoices[0];
  };

  // Remove TODO o markdown/símbolos para leitura natural
  const cleanForSpeech = (text: string): string => {
    let s = text;

    // Blocos de código (```...```) — remove completamente
    s = s.replace(/```[\s\S]*?```/g, ' ');
    // Código inline `code` → code
    s = s.replace(/`([^`]+)`/g, '$1');
    // Imagens ![alt](url) — remove
    s = s.replace(/!\[[^\]]*\]\([^)]+\)/g, '');
    // Links [texto](url) → texto
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
    // URLs cruas
    s = s.replace(/https?:\/\/\S+/g, '');
    // Headers markdown (# ## ### etc) — só remove o # mas mantém pausa
    s = s.replace(/^#{1,6}\s+/gm, '');
    s = s.replace(/\n#{1,6}\s+/g, '. ');
    // Negrito/itálico: **texto**, __texto__, *texto*, _texto_, ~~texto~~
    s = s.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
    s = s.replace(/\*\*([^*]+)\*\*/g, '$1');
    s = s.replace(/\*([^*\n]+)\*/g, '$1');
    s = s.replace(/__([^_]+)__/g, '$1');
    s = s.replace(/(?<![a-zA-Z0-9])_([^_\n]+)_(?![a-zA-Z0-9])/g, '$1');
    s = s.replace(/~~([^~]+)~~/g, '$1');
    // Listas com bullets: -, *, + no início de linha
    s = s.replace(/^[\s]*[-+*]\s+/gm, '');
    // Listas numeradas: 1. 2. 3.
    s = s.replace(/^[\s]*\d+\.\s+/gm, '');
    // Blockquotes >
    s = s.replace(/^>\s+/gm, '');
    // Linhas horizontais --- *** ___
    s = s.replace(/^[-*_]{3,}\s*$/gm, '');
    // Tabelas (linhas com | e separadores)
    s = s.replace(/^\|.*\|.*$/gm, '');
    s = s.replace(/\|/g, ' ');
    // Citações ABNT/APA/ISO inteiras
    s = s.replace(/\[(ABNT|APA|ISO|fonte)\][^.\n]*\.?/gi, '');
    // Emojis (range amplo Unicode)
    s = s.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}]/gu, '');
    // Símbolos especiais que TTS pronuncia literalmente
    s = s.replace(/[#*_~`>|]/g, '');
    // Múltiplas quebras de linha viram pausa
    s = s.replace(/\n{2,}/g, '. ');
    s = s.replace(/\n/g, '. ');
    // Múltiplos espaços
    s = s.replace(/\s+/g, ' ');
    // Múltiplos pontos consecutivos
    s = s.replace(/\.{2,}/g, '.');
    s = s.replace(/\.\s*\.\s*\.+/g, '.');
    // Espaço antes de pontuação
    s = s.replace(/\s+([.,!?:;])/g, '$1');
    return s.trim();
  };

  const speakText = (text: string) => {
    if (!voiceEnabled) return;
    if (!('speechSynthesis' in window)) return;

    const clean = cleanForSpeech(text);
    if (!clean) return;

    const voice = pickBestVoice();

    // Quebra em sentenças para evitar "engasgo" — fala uma de cada vez
    const sentences = clean.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0);

    window.speechSynthesis.cancel();

    sentences.forEach((sentence) => {
      const utter = new SpeechSynthesisUtterance(sentence);
      utter.lang = 'pt-BR';
      utter.rate = 0.98;     // levemente mais devagar = mais natural
      utter.pitch = 1.0;
      utter.volume = 1.0;
      if (voice) utter.voice = voice;
      window.speechSynthesis.speak(utter);
    });
  };

  // Garante que vozes estejam carregadas (Chrome lazy-load)
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const sync = () => window.speechSynthesis.getVoices();
    sync();
    window.speechSynthesis.onvoiceschanged = sync;
    return () => { window.speechSynthesis.onvoiceschanged = null as any; };
  }, []);

  // Extrai nome de mensagens como "Meu nome é João", "Sou Maria", "Pode me chamar de Pedro"
  const tryExtractName = (text: string): string | null => {
    const patterns = [
      /(?:meu nome\s+(?:é|eh)|me chamo|sou (?:o |a )?|pode me chamar de|aqui (?:é|eh) o?a? ?)\s*([A-ZÁÉÍÓÚÂÊÔÃÕÇ][a-záéíóúâêôãõç]+(?:\s+[A-ZÁÉÍÓÚÂÊÔÃÕÇ][a-záéíóúâêôãõç]+)?)/i,
      /^([A-ZÁÉÍÓÚÂÊÔÃÕÇ][a-záéíóúâêôãõç]+)\.?\s*$/,
    ];
    for (const p of patterns) {
      const m = text.match(p);
      if (m && m[1]) return m[1].trim();
    }
    return null;
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const pergunta = input;
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: pergunta,
      timestamp: new Date(),
    };

    // Se ainda não temos nome, tenta extrair desta mensagem
    let nomeAtual = interlocutorName;
    if (!nomeAtual) {
      const extracted = tryExtractName(pergunta);
      if (extracted) {
        nomeAtual = extracted;
        setInterlocutorName(extracted);
        localStorage.setItem('liz_interlocutor_name', extracted);
      }
    }

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // Histórico para enviar ao backend (últimas 8 mensagens)
    const historico = messages
      .slice(-8)
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const token = lerToken('saas') || '';
      const resp = await fetch('/api/ia/chat/verified', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          pergunta,
          nomeInterlocutor: nomeAtual,
          historico,
          // Liz é a assistente EXCLUSIVA dos gestores do SaaS: sempre habilitada
          // na chave da plataforma, nunca na chave BYO de uma creche.
          contexto: 'saas',
        }),
      });

      // Sessão expirada / trocada por outra aba (o token do painel da creche
      // não fala com a Liz do SaaS): mensagem clara, nunca o genérico
      // "problema técnico" nem o aviso de chave de IA da creche.
      if (resp.status === 401 || resp.status === 403) {
        const motivo = await resp.json().catch(() => null);
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `🔒 ${motivo?.message || 'Sua sessão expirou por segurança.'} Saia e faça login novamente no painel do SaaS para continuar conversando comigo — sua mensagem não foi perdida, é só reenviar depois de reautenticar.`,
          timestamp: new Date(),
        }]);
        return;
      }

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      const primaryFonte = data.fontes && data.fontes.length > 0 ? data.fontes[0] : null;
      const respostaTexto = data.resposta || 'Desculpe, não consegui processar agora. Tenta novamente?';
      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: respostaTexto,
        timestamp: new Date(),
        expertise: primaryFonte?.categoria as Message['expertise'],
        source: primaryFonte ? { title: primaryFonte.titulo, url: primaryFonte.url } : undefined,
      };
      setMessages(prev => [...prev, assistantMsg]);
      speakText(respostaTexto);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Tive um problema técnico ao processar sua mensagem agora. Pode tentar novamente em instantes?',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  // ── Copiar / Baixar / Salvar documento gerado ───────────────────────────
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('✅ Copiado para a área de transferência!');
    } catch {
      alert('Erro ao copiar. Selecione o texto manualmente.');
    }
  };

  const downloadAsTxt = (text: string, title: string) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = title.replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60);
    a.download = `${safeName || 'documento-liz'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openSaveDialog = (content: string, messageId: string) => {
    // Sugere título a partir da primeira linha ou primeiras palavras
    const firstLine = content.split('\n')[0].replace(/[#*]/g, '').trim();
    const suggested = firstLine.length > 5 && firstLine.length < 100
      ? firstLine
      : `Documento de ${new Date().toLocaleDateString('pt-BR')}`;
    setSaveDialogTitle(suggested);
    setSaveDialogPurpose('');
    setSaveDialogOpen({ open: true, content, messageId });
  };

  const handleSaveDocument = async () => {
    if (!saveDialogTitle.trim()) {
      alert('Informe um título.');
      return;
    }
    try {
      const token = lerToken('saas') || '';
      const resp = await fetch('/api/ia/generated-docs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title: saveDialogTitle,
          content: saveDialogOpen.content,
          purpose: saveDialogPurpose,
          interlocutor_name: interlocutorName,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      alert('✅ Documento salvo! Acesse em "📄 Meus Documentos" no menu superior.');
      setSaveDialogOpen({ open: false, content: '' });
    } catch (err) {
      alert('Erro ao salvar documento. Tente novamente.');
    }
  };

  return (
    <div className="saas-liz-chat-container">
      <header
        className="chat-header"
        onMouseDown={onDragStart}
        style={{ cursor: onDragStart ? 'grab' : 'default' }}
      >
        <div className="chat-header-info">
          <div className="liz-avatar-glow">
            <img src="/liz_avatar.png" alt="Liz" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
          </div>
          <div>
            <h3>Liz • Super Assistente SaaS</h3>
            <div className="online-badge">
              <span className="dot"></span> Online • Master DNA Ativo
            </div>
          </div>
        </div>
        <div className="expertises-badges">
          <span className="badge law"><Scale size={12} /> Jurídico</span>
          <span className="badge marketing"><BarChart3 size={12} /> Marketing</span>
          <span className="badge tech"><Zap size={12} /> Digital</span>
        </div>
        <button
          onClick={() => { setVoiceEnabled(!voiceEnabled); if (voiceEnabled) window.speechSynthesis?.cancel(); }}
          title={voiceEnabled ? 'Desligar voz da LIZ' : 'Ligar voz da LIZ'}
          style={{
            background: voiceEnabled ? '#ff4081' : 'transparent',
            color: voiceEnabled ? '#fff' : '#ff4081',
            border: '1px solid #ff4081',
            width: 36, height: 36, borderRadius: '50%',
            cursor: 'pointer', marginRight: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16,
          }}
        >
          {voiceEnabled ? '🔊' : '🔈'}
        </button>
      </header>

      <div className="messages-area" ref={scrollRef}>
        {messages.map((msg) => (
          <div key={msg.id} className={`message-bubble ${msg.role}`}>
            <div className={`avatar ${msg.role}`}>
              {msg.role === 'assistant'
                ? <img src="/liz_avatar.png" alt="Liz" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                : <User size={18} />}
            </div>
            <div className="content-wrapper">
              {msg.expertise && (
                <div className={`expertise-tag ${msg.expertise}`}>
                  {msg.expertise === 'law' && <Scale size={10} />}
                  {msg.expertise === 'marketing' && <BarChart3 size={10} />}
                  {msg.expertise === 'education' && <Info size={10} />}
                  {msg.expertise.toUpperCase()}
                </div>
              )}
              <div className="text">{msg.content}</div>
              {msg.source && (
                <a href={msg.source.url} target="_blank" rel="noopener noreferrer" className="source-link">
                  <Link size={12} /> {msg.source.title}
                </a>
              )}
              {msg.role === 'assistant' && msg.content.length > 80 && (
                <div className="msg-actions" style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => copyToClipboard(msg.content)}
                    title="Copiar texto"
                    style={{ background: '#fff', border: '1px solid #fecede', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Copy size={12} /> Copiar
                  </button>
                  <button
                    onClick={() => downloadAsTxt(msg.content, 'documento-liz')}
                    title="Baixar como TXT"
                    style={{ background: '#fff', border: '1px solid #fecede', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Download size={12} /> Baixar
                  </button>
                  <button
                    onClick={() => openSaveDialog(msg.content, msg.id)}
                    title="Salvar nos meus documentos"
                    style={{ background: '#ff4081', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Save size={12} /> Salvar Documento
                  </button>
                </div>
              )}
              <div className="timestamp">{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="message-bubble assistant typing">
            <div className="avatar assistant"><img src="/liz_avatar.png" alt="Liz" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /></div>
            <div className="content-wrapper">
              <div className="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}
        {isSearchingWeb && (
          <div className="web-search-status">
            <div className="spinner"></div>
            <span>Liz está consultando fontes oficiais na web...</span>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <footer className="chat-input-area">
        <div className="input-container">
          <input 
            type="text" 
            placeholder="Pergunte sobre contratos, marketing de SaaS ou regulação..." 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          />
          <div className="action-buttons">
            <button
              className="mic-btn"
              onClick={toggleMic}
              title={isListening ? 'Parar de ouvir' : 'Falar com a LIZ (clique e fale)'}
              aria-label="Conversar por áudio"
              style={isListening ? { background: '#ff4081', color: '#fff', animation: 'pulse-pink 1.2s infinite' } : {}}
            >
              <Mic size={20} />
            </button>
            <button 
              className={`send-btn ${input.trim() ? 'active' : ''}`} 
              onClick={handleSend}
              aria-label="Enviar mensagem"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
        <p className="footer-note">DNA Liz V2.0 Multimodal • Conhecimento Jurídico-Estratégico Aplicado</p>
      </footer>

      {/* Modal: Salvar Documento */}
      {saveDialogOpen.open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 100000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setSaveDialogOpen({ open: false, content: '' })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 24, width: '90%',
              maxWidth: 500, boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#ff4081' }}>💾 Salvar Documento</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Título do documento *</label>
              <input
                type="text"
                value={saveDialogTitle}
                onChange={(e) => setSaveDialogTitle(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }}
                placeholder="Ex: Modelo de Termo de Consentimento LGPD"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Finalidade / Para que será usado</label>
              <textarea
                value={saveDialogPurpose}
                onChange={(e) => setSaveDialogPurpose(e.target.value)}
                rows={2}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                placeholder="Ex: Modelo para coletar consentimento de pais na matrícula"
              />
            </div>
            <div style={{ marginBottom: 16, fontSize: 12, color: '#888' }}>
              {interlocutorName && <>Interlocutor: <strong>{interlocutorName}</strong> · </>}
              Data: <strong>{new Date().toLocaleDateString('pt-BR')}</strong>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSaveDialogOpen({ open: false, content: '' })}
                style={{ padding: '8px 16px', border: '1px solid #ddd', background: '#fff', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveDocument}
                style={{ padding: '8px 16px', border: 'none', background: '#ff4081', color: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
